import React, { useState, useEffect, useRef, useCallback, useContext, LegacyRef } from 'react';
import classNames from 'classnames';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import HolderOutlined from '@ant-design/icons/HolderOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import ExpandOutlined from '@ant-design/icons/ExpandOutlined';
import CompressOutlined from '@ant-design/icons/CompressOutlined';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import { Button } from 'antd';
import { HubServiceState } from '@kemu-io/kemu-core/types';
import { useSelector, useDispatch } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import styles from './ServiceHelpPanel.module.css';
import HubServiceHelpContent from './HubServiceHelpContent/HubServiceHelpContent';
import NativeWidgetHelpContent from './NativeWidgetHelpContent/NativeWidgetHelpContent';
import CustomMarkdownContent from './CustomMarkdownContent/CustomMarkdownContent';
import HelpPanelSearch from './HelpPanelSearch/HelpPanelSearch';
import HelpPanelTips from './HelpPanelTips/HelpPanelTips';
import { HelpServicePanelRef } from './types';
import { LogicMapperContext, ShowHelpConfig } from '@common/context/LogicMapperContext/LogicMapperContext';
import useWidgetState from '@hooks/useWidgetState';
import 'react-resizable/css/styles.css';
import { selectLogicMapperSettings, setLogicMapperSettingValue } from '@src/features/interface/interfaceSlice';
import CardWithBorder from '@components/cardWithBorder/cardWithBorder';
import Spinner from '@components/spinner/spinner';
import { isDevEnvironment, humaniseUpperCasedString } from '@common/utils';
import { useTranslation } from '@hooks/index';
import useSelectMdFile from '@hooks/useSelectMdFile';


type Props = {
  info: ShowHelpConfig;
}


const maxPanelWidth = 2240;
const minPanelSize = { width: 300, height: 350 };
const PANEL_MARGIN = 10;

const ServiceHelpPanel = (props: Props) => {
  const cW = useTranslation('CommonWords');
  const t = useTranslation('ServiceHelpPanel');
  const { widgetId, recipeId, thingRecipeId, isNativeWidget, type, customMarkdown, customTitle } = props.info;

  // Detect platform help
  const isPlatformHelp = widgetId === 'platform-help';
  const contentPanelRef = useRef<HelpServicePanelRef>(null);
  const [showBackButton, setShowBackButton] = useState(false);
  const { onShowHelp } = useContext(LogicMapperContext);
  const { state } = useWidgetState<HubServiceState>(recipeId, thingRecipeId, widgetId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [normalSize, setNormalSize] = useState<{ width: number, height: number }>({ ...minPanelSize });
  const [normalPosition, setNormalPosition] = useState<{ x: number, y: number }>({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDraggingKemuSnippet, setIsDraggingKemuSnippet] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchResults, setSearchResults] = useState({ count: 0, currentIndex: 0 });
  const loadLocalMdFile = useSelectMdFile();
  const dispatch = useDispatch();
  const userSettings = useSelector(selectLogicMapperSettings);
  const defaultPanelPosition = userSettings.serviceHelpPanelPosition;
  const defaultPanelSize = userSettings.serviceHelpPanelSize || minPanelSize;
  const [panelSize, setPanelSize] = useState<{ width: number, height: number }>(defaultPanelSize);
  const [panelPosition, setPanelPosition] = useState(defaultPanelPosition);
  const { ref, width, height } = useResizeDetector();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const inDevMode = isDevEnvironment();

  const closePanel = () => {
    onShowHelp(null);
  };

  /**
   * Hides the panel if in expanded mode
   */
  const onDraggingKemuSnippet = useCallback((isDragging: boolean) => {
    setIsDraggingKemuSnippet(isDragging);
  }, []);


  const popContentHistory = useCallback(() => {
    contentPanelRef.current?.popContentHistory();
  }, []);

  const handleSearchResultsChange = useCallback((count: number, currentIndex: number) => {
    setSearchResults({ count, currentIndex });
  }, []);


  const toggleExpand = () => {
    if (!isExpanded) {
      // Save current size and position before expanding
      setNormalSize({ ...panelSize });
      setNormalPosition({ ...panelPosition });

      // Calculate expanded size (80% of viewport)
      if (width && height) {
        const expandedWidth = Math.min(Math.floor(width * 0.8), maxPanelWidth);
        const expandedHeight = Math.floor(height * 0.9); // 90%

        // Center the panel
        const centerX = Math.floor((width - expandedWidth) / 2);
        const centerY = Math.floor((height - expandedHeight) / 2);

        setPanelSize({
          width: expandedWidth,
          height: expandedHeight
        });

        setPanelPosition({
          x: centerX,
          y: centerY
        });
      }
    } else {
      // Restore previous size and position
      setPanelSize(normalSize);
      setPanelPosition(normalPosition);

      // Save position and size in redux
      dispatch(setLogicMapperSettingValue({
        key: 'serviceHelpPanelPosition',
        value: normalPosition,
      }));
      dispatch(setLogicMapperSettingValue({
        key: 'serviceHelpPanelSize',
        value: normalSize,
      }));
    }

    setIsExpanded(!isExpanded);
  };

  const handleDrag = useCallback(() => {
    setIsDragging(true);
  }, []);


  const handleFileSelection = async () => {
    const { file } = await loadLocalMdFile();
    if (file) {
      contentPanelRef.current?.renderCustomContent(file.content);
    }
  };


  const handleResize = (_: React.SyntheticEvent, data: ResizeCallbackData) => {
    setPanelSize({
      width: Math.max(minPanelSize.width, data.size.width),
      height: Math.max(minPanelSize.height, data.size.height),
    });
  };

  const handleResizeStop = (_: React.SyntheticEvent, data: ResizeCallbackData) => {
    setIsDragging(false);
    // Save the final size to redux, ensuring it meets minimum constraints
    const finalSize = {
      width: Math.max(minPanelSize.width, data.size.width),
      height: Math.max(minPanelSize.height, data.size.height),
    };
    // Dispatch only if not expanded (expanded size shouldn't be saved)
    if (!isExpanded) {
        dispatch(setLogicMapperSettingValue({
          key: 'serviceHelpPanelSize',
          value: finalSize,
        }));
    }
  };

  const handleStopDragging = (_: DraggableEvent, data: DraggableData) => {
    const newPosition = {
      x: data.x,
      y: data.y,
    };

    setPanelPosition(newPosition);
    setIsDragging(false);

    // Only save position in redux if not in expanded mode
    if (!isExpanded) {
      dispatch(setLogicMapperSettingValue({
        key: 'serviceHelpPanelPosition',
        value: newPosition,
      }));
    }
  };

  // Set up the resize detector reference
  useEffect(() => {
    if (containerRef.current) {
      ref.current = containerRef.current.parentElement;
    }
  }, [ref]);

  // Prevent panel from being outside the visible workspace
  useEffect(() => {
    if (width && height && !isExpanded) {
      // Use the current panelSize state for calculations
      const currentWidth = panelSize.width;
      const currentHeight = panelSize.height;

      setPanelPosition((current) => {
        const panelX = width && width < (current.x + currentWidth + PANEL_MARGIN)
          ? Math.max(0, width - currentWidth - PANEL_MARGIN) // Ensure not negative
          : Math.max(0, current.x); // Ensure not negative

        const panelY = height && height < (current.y + currentHeight + PANEL_MARGIN)
          ? Math.max(0, height - currentHeight - PANEL_MARGIN) // Ensure not negative
          : Math.max(0, current.y); // Ensure not negative

        return {
          x: panelX,
          y: panelY,
        };
      });
    }
  }, [width, height, panelSize.width, panelSize.height, isExpanded]);

  return (
    <>
      {/* Backdrop Element */}
      <div
        className={classNames(styles.Backdrop, {
          [styles.BackdropActive]: isExpanded,
          [styles.Invisible]: isDraggingKemuSnippet && isExpanded,
        })}
        // Optionally close the panel when clicking the backdrop
        // onClick={isExpanded ? toggleExpand : undefined}
      />
      <Draggable
        onStop={handleStopDragging}
        onDrag={handleDrag}
        handle={`.${styles.DragHandle}`}
        bounds="parent"
        position={panelPosition}
        disabled={isExpanded}
        // IMPORTANT: set the node to avoid findDOMNode warning from react-dnd
        nodeRef={containerRef}
      >
        <Resizable
          height={panelSize.height}
          width={panelSize.width}
          minConstraints={[minPanelSize.width, minPanelSize.height]}
          resizeHandles={isExpanded ? [] : ['se']}
          onResize={handleResize}
          onResizeStart={() => setIsDragging(true)}
          onResizeStop={handleResizeStop}
        >
          <CardWithBorder
            ref={containerRef}
            bodyClassName={styles.CardBody}
            headerContent={
              <div className={classNames(styles.DragHandle, { [styles.IsExpanded]: isExpanded })}>
                <div className={styles.DragHandleLeft}>
                  {!isExpanded && (
                    <span className={styles.DragIcon}>
                      <HolderOutlined />
                    </span>
                  )}
                  <span className={styles.DragText}>
                    <h4>
                      {customMarkdown ? (
                        t('Title', undefined, { shortName: customTitle || t('CustomWidgetPreviewTitle') })
                      ) : isNativeWidget ? (
                        t('Title', undefined, { shortName: humaniseUpperCasedString(type) })
                      ) : (
                        t('Title', undefined, { shortName: state.service?.shortTitle || cW('Service') })
                      )}

                      {inDevMode && (
                        <Button
                          icon={<FolderOutlined />}
                          size="small"
                          onClick={handleFileSelection}
                          title="Select markdown file"
                          className={styles.FolderButton}
                        />
                      )}
                    </h4>
                  </span>
                </div>
                <div className={styles.DragHandleRight}>
                  <Button
                    type="text"
                    size="small"
                    icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                    onClick={toggleExpand}
                    title={isExpanded ? cW('Contract') : cW('Expand')}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={closePanel}
                    title={cW('Close')}
                  />
                </div>
              </div>
            }
            className={classNames(styles.CardContainer, {
              [styles.IsDragging]: isDragging,
              [styles.Invisible]: isDraggingKemuSnippet && isExpanded,
            })}
            style={{
              width: panelSize.width,
              height: panelSize.height,
            }}
          >
            <>
              {showBackButton && !isLoading && (
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={popContentHistory}
                  size='large'
                  shape='circle'
                  className={styles.BackButton}
                  title={cW('Back')}
                />
              )}

              <HelpPanelSearch
                searchContainer={scrollableContentRef.current}
                isVisible={isSearchVisible}
                onVisibilityChange={setIsSearchVisible}
                onSearchResultsChange={handleSearchResultsChange}
              />

              <Spinner loading={isLoading} size='large' wrapperClassName={styles.Spinner}>
                <div
                  ref={contentContainerRef}
                  className={classNames(styles.Content, {
                    [styles.IsExpanded]: isExpanded,
                    [styles.WithSearch]: isSearchVisible
                  })}
                  tabIndex={-1}
                >
                  <div ref={scrollableContentRef} className={styles.ScrollableContent}>
                    {customMarkdown && (
                      <CustomMarkdownContent
                        customMarkdown={customMarkdown}
                        onDraggingKemuSnippet={onDraggingKemuSnippet}
                        onLoadingContent={setIsLoading}
                        onShowBackButton={setShowBackButton}
                      />
                    )}

                    {!customMarkdown && !isNativeWidget && state.service && (
                      <HubServiceHelpContent
                        serviceName={state.service?.name}
                        serviceVersion={state.service?.version}
                        recipeId={recipeId}
                        thingRecipeId={thingRecipeId}
                        onDraggingKemuSnippet={onDraggingKemuSnippet}
                        onLoadingContent={setIsLoading}
                        onShowBackButton={setShowBackButton}
                        widgetId={widgetId}
                        ref={contentPanelRef}
                      />
                    )}

                    {!customMarkdown && (isNativeWidget || isPlatformHelp) && (
                      <NativeWidgetHelpContent
                        onDraggingKemuSnippet={onDraggingKemuSnippet}
                        onLoadingContent={setIsLoading}
                        onShowBackButton={setShowBackButton}
                        type={type}
                        recipeId={recipeId}
                        thingRecipeId={thingRecipeId}
                        widgetId={widgetId}
                        ref={contentPanelRef}
                        isPlatformHelp={isPlatformHelp}
                      />
                    )}
                  </div>
                </div>
              </Spinner>

              <HelpPanelTips
                isSearchVisible={isSearchVisible}
                currentSearchIndex={searchResults.currentIndex}
                totalSearchResults={searchResults.count}
              />
            </>
          </CardWithBorder>
        </Resizable>
      </Draggable>
    </>
  );
};

export default ServiceHelpPanel;
